import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Invoice, Customer, Vehicle, Appointment } from "@/types";
import { PaymentOutput } from "@/types/payment.types";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import { WE_CLEAN_LOGO } from "@/assets/logo";

// Company information
const COMPANY_INFO = {
  name: "WeClean Car Wash",
  tagline: "We come to you",
  phone: "+974 7090 8373",
  website: "www.weclean.com.qa",
  email: "info@weclean.com.qa",
};

interface GenerateInvoicePdfParams {
  invoice: Invoice;
  customer: Customer;
  vehicles: Vehicle[];
  appointment?: Appointment;
}

export function generateInvoicePdf({
  invoice,
  customer,
  vehicles,
  appointment,
}: GenerateInvoicePdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Colors
    const black = [0, 0, 0];
    const darkGray = [64, 64, 64];

    // Set font
    doc.setFont("helvetica");

    // --- Header Section ---
    // Logo (Top Left)
    const logoWidth = 50; // Adjusted size (approx 2:1 ratio)
    const logoHeight = 25;
    try {
      doc.addImage(
        WE_CLEAN_LOGO,
        "JPEG",
        margin,
        margin,
        logoWidth,
        logoHeight,
      );
    } catch (e) {
      console.error("Error adding logo:", e);
      // Fallback: text if image fails
      doc.setFontSize(10);
      doc.text("WeClean", margin, margin + 10);
    }

    // Invoice Title (Top Right)
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Invoice", pageWidth - margin, margin + 12, { align: "right" });

    // Invoice Details (Below Title, Right Aligned)
    yPosition = margin + 25; // Start below title
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);

    // Helper for right aligned text
    const rightAlignText = (text: string, y: number) => {
      doc.text(text, pageWidth - margin, y, { align: "right" });
    };

    rightAlignText(`Invoice number: ${invoice.invoiceNumber}`, yPosition);

    yPosition += 6;
    const issueDate = format(new Date(invoice.date), "MMMM dd, yyyy");
    rightAlignText(`Date of issue: ${issueDate}`, yPosition);

    yPosition += 6;
    rightAlignText(`Date due: ${issueDate}`, yPosition);

    // --- Info Section ---
    // Company Info (Left Side, Below Logo)
    yPosition = margin + 35; // Adjust based on logo size
    const leftColumnY = yPosition;

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(COMPANY_INFO.name, margin, yPosition);

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text(COMPANY_INFO.tagline, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.phone, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.email, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.website, margin, yPosition);

    // Bill To (Right Side, Below Invoice Details header area roughly)
    const rightColumnY = leftColumnY; // Start at same Y as company info
    yPosition = rightColumnY;

    // Header "Bill to:"
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Bill to:", pageWidth - margin, yPosition, { align: "right" });

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold"); // Bold for customer name
    doc.setTextColor(...black);
    doc.text(customer.name, pageWidth - margin, yPosition, { align: "right" });

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    if (customer.city) {
      doc.text(customer.city, pageWidth - margin, yPosition, {
        align: "right",
      });
      yPosition += 6;
    }
    if (customer.email) {
      doc.text(customer.email, pageWidth - margin, yPosition, {
        align: "right",
      });
      yPosition += 6;
    }

    // Ensure we start below the lowest column
    yPosition = Math.max(yPosition, leftColumnY + 30) + 10;

    // Amount due summary (Prominent, Left Aligned now as per standard, or keep custom?)
    // Let's keep it prominent but maybe left aligned looks better with the table below
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    const amountDueText = `${formatCurrency(invoice.total)} due ${issueDate}`;
    doc.text(amountDueText, margin, yPosition);

    // Line items table
    yPosition += 10;

    // Prepare table data
    const tableData = invoice.items.map((item) => {
      // Build description - first line is the item name
      const descriptionLines: string[] = [item.name];

      // Add description if available
      if (item.description) {
        descriptionLines.push(item.description);
      }

      // Add service period for subscriptions
      if (appointment && appointment.packageType === "subscription") {
        const startDate = format(new Date(appointment.date), "MMM dd");
        // Calculate end date (assuming monthly subscription)
        const endDate = new Date(appointment.date);
        endDate.setMonth(endDate.getMonth() + 1);
        const endDateFormatted = format(endDate, "MMM dd, yyyy");
        descriptionLines.push(
          `Service Period: ${startDate} - ${endDateFormatted}`,
        );
      }

      return [
        descriptionLines.join("\n"),
        item.quantity.toString(),
        formatCurrency(item.unitPrice),
        formatCurrency(item.total),
      ];
    });

    // Create table
    autoTable(doc, {
      startY: yPosition,
      head: [["Description", "Qty", "Unit price", "Amount"]],
      body: tableData,
      theme: "plain",
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontStyle: "bold",
        fontSize: 10,
        lineColor: [220, 220, 220],
        lineWidth: 0.5,
        halign: "left",
      },
      bodyStyles: {
        fillColor: [255, 255, 255],
        textColor: black,
        fontSize: 10,
        lineColor: [240, 240, 240],
        lineWidth: 0.3,
      },
      styles: {
        cellPadding: { top: 6, bottom: 6, left: 5, right: 5 },
        fontSize: 10,
        font: "helvetica",
        overflow: "linebreak",
        cellWidth: "wrap",
      },
      columnStyles: {
        0: { cellWidth: "auto", halign: "left", valign: "top" },
        1: { cellWidth: 18, halign: "center", valign: "middle" },
        2: { cellWidth: 32, halign: "right", valign: "middle" },
        3: { cellWidth: 32, halign: "right", valign: "middle" },
      },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });

    // Get final Y position after table
    const finalY = (doc as any).lastAutoTable.finalY || yPosition + 50;

    // Total charges breakdown (right-aligned)
    let totalY = finalY + 12;
    const labelX = pageWidth - margin - 50;
    const valueX = pageWidth - margin;

    // Subtotal
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Subtotal:", labelX, totalY, { align: "right" });
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.subtotal), valueX, totalY, {
      align: "right",
    });

    // Price adjustment (if any). Backend discount is inverted: positive
    // reduces the price, negative is a surcharge. Show both so the line
    // items reconcile with the total.
    if (invoice.discount !== 0) {
      totalY += 6;
      const isSurcharge = invoice.discount < 0;
      doc.setTextColor(...darkGray);
      doc.text(
        isSurcharge
          ? "Price Adjustment (Surcharge):"
          : "Price Adjustment (Discount):",
        labelX,
        totalY,
        { align: "right" },
      );
      doc.setTextColor(...black);
      doc.text(
        `${isSurcharge ? "+" : "-"}${formatCurrency(Math.abs(invoice.discount))}`,
        valueX,
        totalY,
        { align: "right" },
      );
    }

    // Platinum Member Discount (if any)
    if (invoice.platinumDiscount && invoice.platinumDiscount > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text("Platinum Member Discount (15%):", labelX, totalY, { align: "right" });
      doc.setTextColor(217, 119, 6); // Amber color for platinum
      doc.text(`-${formatCurrency(invoice.platinumDiscount)}`, valueX, totalY, {
        align: "right",
      });
    }

    // Tax (if any)
    if (invoice.tax > 0) {
      totalY += 6;
      doc.setTextColor(...darkGray);
      doc.text("Tax:", labelX, totalY, { align: "right" });
      doc.setTextColor(...black);
      doc.text(formatCurrency(invoice.tax), valueX, totalY, { align: "right" });
    }

    // Total
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Total:", labelX, totalY, { align: "right" });
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: "right" });

    // Amount due
    totalY += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text("Amount due:", labelX, totalY, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(formatCurrency(invoice.total), valueX, totalY, { align: "right" });

    // Save PDF
    const fileName = `Invoice-${invoice.invoiceNumber}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating invoice PDF:", error);
    throw new Error("Failed to generate invoice PDF");
  }
}

interface GeneratePaymentReceiptPdfParams {
  payment: PaymentOutput;
  customer?: Customer | null;
  vehicles?: Vehicle[];
}

export function generatePaymentReceiptPdf({
  payment,
  customer,
  vehicles = [],
}: GeneratePaymentReceiptPdfParams): void {
  try {
    // Create PDF document
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    let yPosition = margin;

    // Colors
    const black = [0, 0, 0];
    const darkGray = [64, 64, 64];

    // Set font
    doc.setFont("helvetica");

    // --- Header Section ---
    // Logo (Top Left)
    const logoWidth = 50; // Adjusted size (approx 2:1 ratio)
    const logoHeight = 25;
    try {
      doc.addImage(
        WE_CLEAN_LOGO,
        "JPEG",
        margin,
        margin,
        logoWidth,
        logoHeight,
      );
    } catch (e) {
      console.error("Error adding logo:", e);
      // Fallback
      doc.setFontSize(10);
      doc.text("WeClean", margin, margin + 10);
    }

    // Receipt Title (Top Right)
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Payment Receipt", pageWidth - margin, margin + 12, {
      align: "right",
    });

    // Payment Details Header (Below Title, Right Aligned)
    yPosition = margin + 25;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);

    const rightAlignText = (text: string, y: number) => {
      doc.text(text, pageWidth - margin, y, { align: "right" });
    };

    rightAlignText(
      `Payment ID: ${payment.id.slice(0, 8).toUpperCase()}`,
      yPosition,
    );

    yPosition += 6;
    const displayDate = payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt
      ? format(new Date(payment.appointmentScheduledAt), "MMMM dd, yyyy HH:mm")
      : payment.date
        ? format(new Date(payment.date), "MMMM dd, yyyy HH:mm")
        : null;
    if (displayDate) {
      rightAlignText(`Date: ${displayDate}`, yPosition);
    }

    // --- Info Section ---
    // Company Info (Left Side, Below Logo)
    yPosition = margin + 35;
    const leftColumnY = yPosition;

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text(COMPANY_INFO.name, margin, yPosition);

    yPosition += 6;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    doc.text(COMPANY_INFO.tagline, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.phone, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.email, margin, yPosition);

    yPosition += 6;
    doc.text(COMPANY_INFO.website, margin, yPosition);

    // Paid By (Right Side, Below Header)
    yPosition = leftColumnY;
    if (customer) {
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(...darkGray);
      doc.text("Paid by:", pageWidth - margin, yPosition, { align: "right" });

      yPosition += 6;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal"); // Bold for customer name?
      doc.setTextColor(...black);
      doc.text(customer.name, pageWidth - margin, yPosition, {
        align: "right",
      });

      yPosition += 6;
      doc.setFontSize(10);
      doc.setTextColor(...darkGray);
      if (customer.city) {
        doc.text(customer.city, pageWidth - margin, yPosition, {
          align: "right",
        });
        yPosition += 6;
      }
      if (customer.email) {
        doc.text(customer.email, pageWidth - margin, yPosition, {
          align: "right",
        });
        yPosition += 6;
      }
    }

    // Reset Y to below the lower column
    yPosition = Math.max(yPosition, leftColumnY + 30) + 10;

    // Payment amount - prominent display
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    const amountText = formatCurrency(payment.paidAmount);
    doc.text(amountText, margin, yPosition);

    // Payment details section
    yPosition += 15;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...black);
    doc.text("Payment Details", margin, yPosition);

    yPosition += 12;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");

    // Payment Status
    doc.setTextColor(...darkGray);
    doc.text("Status:", margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.status, margin + 40, yPosition);

    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text("Payment Mode:", margin, yPosition);
    doc.setTextColor(...black);
    doc.text(payment.mode, margin + 40, yPosition);

    yPosition += 8;
    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text("Reference ID:", margin, yPosition);
    doc.setTextColor(...black);

    // Use bookingRef if available, otherwise fallback to formatted refId
    const refIdDisplay = payment.bookingRef
      ? payment.bookingRef
      : payment.refId
        ? payment.refId.slice(0, 8).toUpperCase()
        : "-";

    doc.text(refIdDisplay, margin + 40, yPosition);

    if (payment.serviceType) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.text("Service Type:", margin, yPosition);
      doc.setTextColor(...black);
      doc.text(
        payment.serviceType === "subscription" ? "Subscription" : "One-Time",
        margin + 40,
        yPosition,
      );
    }

    if (payment.refType === "APPOINTMENT" && payment.appointmentScheduledAt) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.text("Scheduled Date & Time:", margin, yPosition);
      doc.setTextColor(...black);
      doc.text(
        format(new Date(payment.appointmentScheduledAt), "MMM dd, yyyy HH:mm"),
        margin + 55,
        yPosition,
      );
    }

    // Amount breakdown
    yPosition += 12;
    doc.setTextColor(...darkGray);
    doc.text("Total Amount:", margin, yPosition);
    doc.setTextColor(...black);
    doc.text(formatCurrency(payment.totalAmount), margin + 40, yPosition);

    // Price Adjustment (if non-zero)
    if (payment.discount !== 0) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      const isSurcharge = payment.discount < 0;
      doc.text(
        isSurcharge
          ? "Price Adjustment (Surcharge):"
          : "Price Adjustment (Discount):",
        margin,
        yPosition,
      );
      if (isSurcharge) {
        doc.setTextColor(217, 119, 6); // Amber color
        doc.text(
          `+${formatCurrency(Math.abs(payment.discount))}`,
          margin + 60,
          yPosition,
        );
      } else {
        doc.setTextColor(16, 185, 129); // Emerald green color
        doc.text(
          `-${formatCurrency(payment.discount)}`,
          margin + 60,
          yPosition,
        );
      }
    }

    // Platinum Member Discount (only show if > 0)
    if (payment.platinumDiscount > 0) {
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.text("Platinum Discount (15%):", margin, yPosition);
      doc.setTextColor(217, 119, 6); // Amber color for platinum
      doc.text(`-${formatCurrency(payment.platinumDiscount)}`, margin + 60, yPosition);
    }

    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text("Paid Amount:", margin, yPosition);
    doc.setTextColor(0, 128, 0); // Green color
    doc.text(formatCurrency(payment.paidAmount), margin + 40, yPosition);

    yPosition += 8;
    doc.setTextColor(...darkGray);
    doc.text("Due Amount:", margin, yPosition);
    doc.setTextColor(255, 140, 0); // Orange color
    doc.text(formatCurrency(payment.dueAmount), margin + 40, yPosition);

    // Vehicle information (if available)
    if (vehicles.length > 0) {
      yPosition += 15;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...black);
      doc.text(
        `Vehicle${vehicles.length > 1 ? "s" : ""} Information`,
        margin,
        yPosition,
      );

      yPosition += 8;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      vehicles.forEach((vehicle, index) => {
        if (index > 0) yPosition += 6; // Add spacing between vehicles

        doc.setTextColor(...darkGray);
        doc.text(`Vehicle ${index + 1}:`, margin, yPosition);
        doc.setTextColor(...black);
        const vehicleText = vehicle.plateNumber
          ? `${vehicle.make} ${vehicle.model} - ${vehicle.plateNumber}`
          : `${vehicle.make} ${vehicle.model}`;
        doc.text(vehicleText, margin + 30, yPosition);
        yPosition += 6;
      });

      yPosition -= 6; // Remove extra spacing after last vehicle
    }

    // Footer line
    const finalY = yPosition + 20;
    doc.setDrawColor(...darkGray);
    doc.setLineWidth(0.5);
    doc.line(margin, finalY, pageWidth - margin, finalY);

    yPosition = finalY + 8;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...darkGray);
    // Center alignment based on pageWidth
    doc.text("Thank you for your payment!", pageWidth / 2, yPosition, {
      align: "center",
    });

    // Save PDF
    const fileName = `Payment-Receipt-${payment.id.slice(0, 8)}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error("Error generating payment receipt PDF:", error);
    throw new Error("Failed to generate payment receipt PDF");
  }
}
